import React from 'react';

import { FieldArray, useFormikContext } from 'formik';

import { iaRa, optionify } from 'lib/helpers/utility';
import { useStates } from 'lib/hooks/queries/states/useStates';

import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Button from 'components/shared/Button/Button';
import Select from 'components/shared/Select/Select';
import SectionList from 'components/practice/UserPreferences/components/SectionList';

const emptyLocation = () => ({
  label: '',
  address: { address_ln_1: '', address_ln_2: '', city: '', state: '', zip: '' }
});

const AdditionalLocations = () => {
  const { data } = useStates();
  const states = iaRa(data?.states);
  const { values, errors, touched, setFieldValue } = useFormikContext();

  const findStateName = (value) => states.find((state) => state?.id == value)?.name;

  return (
    <SectionList
      title="Additional locations"
      description="Add the other offices patients can be seen at. The address of the location selected on an appointment is the one shared with the patient.">
      <FieldArray
        name="locations"
        render={(arrayHelpers) => (
          <div className="flex flex-col gap-6">
            {values.locations?.map((location, index) => {
              const labelError =
                touched.locations?.[index]?.label && errors.locations?.[index]?.label;
              return (
                <div key={index} className="flex w-full items-start gap-3">
                  <div className="grid flex-1 grid-cols-2 gap-4">
                    <Input
                      className="col-span-2"
                      label="Location label"
                      placeholder="Enter location label"
                      name={`locations.${index}.label`}
                      value={location.label}
                      onChange={(e) => setFieldValue(`locations.${index}.label`, e.target.value)}
                      error={labelError}
                      maxLength={255}
                    />
                    <Input
                      label="Address 1"
                      placeholder="Address"
                      name={`locations.${index}.address.address_ln_1`}
                      value={location.address?.address_ln_1}
                      onChange={(e) =>
                        setFieldValue(`locations.${index}.address.address_ln_1`, e.target.value)
                      }
                    />
                    <Input
                      label="Address 2"
                      placeholder="Address 2"
                      name={`locations.${index}.address.address_ln_2`}
                      value={location.address?.address_ln_2}
                      onChange={(e) =>
                        setFieldValue(`locations.${index}.address.address_ln_2`, e.target.value)
                      }
                    />
                    <Input
                      label="City"
                      placeholder="City"
                      name={`locations.${index}.address.city`}
                      value={location.address?.city}
                      onChange={(e) =>
                        setFieldValue(`locations.${index}.address.city`, e.target.value)
                      }
                    />
                    <Select
                      label="State"
                      placeholder="Type and select"
                      inputId={`location-state-select-${index}`}
                      name={`locations.${index}.address.state`}
                      value={{ label: findStateName(location.address?.state) }}
                      onChange={(e) =>
                        setFieldValue(`locations.${index}.address.state`, e?.value || null)
                      }
                      options={optionify(states, 'name', 'id')}
                    />
                    <Input
                      label="Zip code"
                      placeholder="Zip"
                      name={`locations.${index}.address.zip`}
                      value={location.address?.zip}
                      onChange={(e) =>
                        setFieldValue(`locations.${index}.address.zip`, e.target.value)
                      }
                    />
                  </div>
                  <Icon
                    icon="trash-mono"
                    size={16}
                    onClick={() => arrayHelpers.remove(index)}
                    className="mt-7"
                  />
                </div>
              );
            })}

            <div>
              <Button
                text="Add location"
                icon="add-linear"
                color="neutral"
                iconColor="neutral-700"
                variant="tertiary"
                size="small"
                onClick={() => arrayHelpers.push(emptyLocation())}
              />
            </div>
          </div>
        )}
      />
    </SectionList>
  );
};

export default AdditionalLocations;
